import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Slider } from "@mui/material";
import { breakpoint, theme } from "@utilities";

interface PointsSliderProps {
    maxPoints: number;
    pointsPerDollar: number;
    productValue: number;
    onChange: (points: number) => void;
}

export const PointsSlider: React.FC<PointsSliderProps> = ({
    maxPoints,
    pointsPerDollar,
    productValue,
    onChange,
}) => {
    const [value, setValue] = useState(0);
    const [inputValue, setInputValue] = useState("0");
    const maxPointsFromProduct = productValue * pointsPerDollar;
    const maxValue = Math.min(maxPoints, maxPointsFromProduct);
    const hasSeededRef = useRef(false);

    const applyValue = (newVal: number) => {
        const clamped = Math.min(Math.max(0, newVal), maxValue);
        const rounded = Math.round(clamped * 100) / 100;
        setValue(rounded);
        setInputValue(rounded.toFixed(2));
        onChange(rounded);
    };

    useEffect(() => {
        // Start at the most the customer can redeem rather than at zero. maxValue
        // is 0 on the first render or two while the balance and order total load,
        // so seed on the first non-zero value rather than on mount.
        if (!hasSeededRef.current && maxValue > 0) {
            hasSeededRef.current = true;
            applyValue(maxValue);
            return;
        }
        if (value > maxValue) {
            applyValue(maxValue);
        }
    }, [maxValue]);

    const handleSliderChange = (_: any, newValue: number | number[]) => {
        const val = Array.isArray(newValue) ? newValue[0] : newValue;
        applyValue(val);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handleInputBlur = () => {
        const parsed = parseFloat(inputValue);
        if (!isNaN(parsed)) {
            applyValue(parsed);
        } else {
            setInputValue(value.toFixed(2));
        }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleInputBlur();
            (e.target as HTMLInputElement).blur();
        }
        if (e.key === "Escape") {
            setInputValue(value.toFixed(2));
            (e.target as HTMLInputElement).blur();
        }
    };

    return (
        <SliderWrapper>
            <PointsInputRow>
                <span className="label">Apply points</span>
                <PointsInputField
                    type="number"
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleInputKeyDown}
                    min={0}
                    max={maxValue}
                    step={0.01}
                />
                <span className="max-label">/ {maxValue.toFixed(2)} pts</span>
            </PointsInputRow>
            <Slider
                value={value}
                onChange={handleSliderChange}
                min={0}
                max={maxValue}
                step={0.01}
                sx={{
                    width: "100%",
                    height: 8,
                    "& .MuiSlider-root": {
                        height: 8,
                    },
                    "& .MuiSlider-rail": {
                        height: 10,
                        borderRadius: 6,
                        background: "#E8DEF8",
                        opacity: 1,
                        "&::before, &::after": {
                            content: '""',
                            position: "absolute",
                            width: 6,
                            height: 6,
                            backgroundColor: "white",
                            borderRadius: "50%",
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 1,
                        },
                        "&::before": {
                            left: 2,
                        },
                        "&::after": {
                            right: 2,
                        },
                    },
                    "& .MuiSlider-track": {
                        height: 10,
                        borderRadius: 6,
                        background: `linear-gradient(90deg, ${theme.colors.main} 99.99%, #7257FF 100%)`,
                        border: "none",
                        "&::before, &::after": {
                            content: '""',
                            position: "absolute",
                            width: 6,
                            height: 6,
                            backgroundColor: "white",
                            borderRadius: "50%",
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 1,
                        },
                        "&::before": {
                            left: 2,
                        },
                        "&::after": {
                            right: 2,
                        },
                    },
                    "& .MuiSlider-thumb": {
                        width: 20,
                        height: 20,
                        backgroundColor: theme.colors.main,
                        "&:hover, &.Mui-focusVisible": {
                            boxShadow: "none",
                        },
                    },
                    "& .MuiSlider-mark": {
                        display: "none",
                    },
                    "@media (pointer: coarse)": {
                        padding: "0 !important",
                    },
                }}
            />
            <SliderLabels>
                <span>0</span>
                <span>{maxValue.toFixed(2)}</span>
            </SliderLabels>
        </SliderWrapper>
    );
};

const SliderWrapper = styled.div`
    width: 100%;
`;

const PointsInputRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 12px;

    .label {
        font-weight: 500;
        font-size: 14px;
        line-height: 120%;
        color: #131214;
    }

    .max-label {
        font-size: 13px;
        color: #898d8f;
        white-space: nowrap;
    }
`;

const PointsInputField = styled.input`
    padding: 6px 10px;
    border: 1px solid #e6e9eb;
    border-radius: 6px;
    font-weight: 600;
    font-size: 14px;
    line-height: 120%;
    width: 90px;
    text-align: center;
    font-family: "Poppins", sans-serif;
    color: ${theme.colors.highlight};

    &:focus {
        outline: none;
        border-color: ${theme.colors.highlight};
        box-shadow: 0 0 0 2px ${theme.colors.highlight}20;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    &[type="number"] {
        -moz-appearance: textfield;
    }

    ${breakpoint.breakOnlyMobile`
        width: 80px;
    `}
`;

const SliderLabels = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;

    span {
        font-family: "Poppins";
        font-size: 10px;
        line-height: 120%;
        color: #898d8f;

        &:nth-child(2) {
            text-align: center;
        }

        &:nth-child(3) {
            text-align: right;
        }
    }
`;
